import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { RefreshCw, MessageCircle, Loader2, Check, X } from "lucide-react";

export default function PracticeCard({ card, index, onRefresh, refreshing, onCorrect, onIncorrect, incorrectDisabled, flash }) {
  const [flipped, setFlipped] = useState(false);
  if (!card) return <div className="h-52 rounded-3xl bg-card border border-border animate-pulse" />;
  const isStarter = card.type === "starter";
  const label = isStarter ? "Conversation Starter" : (card.tier || "Level 1 · Word");

  return (
    <div className="[perspective:1200px] w-full relative" data-testid={`flashcard-${index}`}>
      <motion.div
        className="relative w-full h-52 cursor-pointer [transform-style:preserve-3d]"
        animate={{ rotateY: flipped ? 180 : 0 }}
        transition={{ type: "spring", stiffness: 260, damping: 20 }}
        onClick={() => setFlipped((f) => !f)}
        data-testid={`flashcard-flip-${index}`}
      >
        {/* front */}
        <div className="absolute inset-0 [backface-visibility:hidden] rounded-3xl bg-card border border-border shadow-sm flex flex-col p-5 pb-9">
          <div className="flex items-center gap-1.5 mb-1 text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
            {isStarter && <MessageCircle className="w-3 h-3 text-primary" />} {label}
          </div>
          <div className="flex-1 flex flex-col items-center justify-center text-center">
            <span className={`font-heading font-black ${isStarter ? "text-lg" : "text-2xl sm:text-3xl"}`}>{card.front}</span>
            {card.pronunciation && <span className="mt-1 text-xs text-muted-foreground">/{card.pronunciation}/</span>}
          </div>
          <span className="text-[11px] text-muted-foreground text-center">Tap to flip</span>
        </div>
        {/* back */}
        <div className="absolute inset-0 [backface-visibility:hidden] [transform:rotateY(180deg)] rounded-3xl bg-primary text-primary-foreground shadow-md flex flex-col items-center justify-center p-5 text-center">
          <span className="text-[10px] uppercase tracking-widest opacity-80 mb-1">{isStarter ? "In your language" : "Meaning"}</span>
          <span className="text-xl sm:text-2xl font-heading font-bold">{card.back}</span>
          {card.example && <span className="mt-2 text-xs italic opacity-85">“{card.example}”</span>}
        </div>
      </motion.div>

      <button
        onClick={onRefresh}
        disabled={refreshing}
        data-testid={`flashcard-refresh-${index}`}
        title="Refresh — a little harder each time"
        className="absolute bottom-2 left-2 z-10 flex items-center gap-1 rounded-full bg-black/5 dark:bg-white/10 backdrop-blur px-2.5 py-1 text-[11px] font-semibold text-foreground/70 hover:text-foreground hover:bg-black/10 transition-colors disabled:opacity-60"
      >
        {refreshing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Refresh
      </button>

      {(onCorrect || onIncorrect) && (
        <div className="absolute bottom-2 right-2 z-10 flex items-center gap-1.5">
          {onIncorrect && (
            <button
              onClick={onIncorrect}
              disabled={incorrectDisabled}
              data-testid={`flashcard-incorrect-${index}`}
              title="Got it wrong — deducts 5 points"
              className="flex items-center justify-center w-7 h-7 rounded-full bg-destructive/10 text-destructive hover:bg-destructive/20 transition-colors disabled:opacity-40"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
          {onCorrect && (
            <button
              onClick={onCorrect}
              data-testid={`flashcard-correct-${index}`}
              title="Got it right — earns 10 points"
              className="flex items-center gap-1 rounded-full bg-secondary text-secondary-foreground px-3 py-1.5 text-[11px] font-bold hover:bg-secondary/90 transition-colors"
            >
              <Check className="w-3.5 h-3.5" /> Correct
            </button>
          )}
        </div>
      )}

      <AnimatePresence>
        {flash && (
          <motion.span
            initial={{ opacity: 0, y: 0, scale: 0.6 }}
            animate={{ opacity: 1, y: -30, scale: 1 }}
            exit={{ opacity: 0 }}
            className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 font-heading font-black text-4xl drop-shadow-lg pointer-events-none ${flash.startsWith("-") ? "text-destructive" : "text-secondary"}`}
            data-testid={`flashcard-flash-${index}`}
          >
            {flash}
          </motion.span>
        )}
      </AnimatePresence>
    </div>
  );
}
